import { FC, useEffect, useState } from 'react';
import { t } from 'i18next';
import { AISuggestedText, CustomModal, StatusBanner } from 'components';
import {
  Box,
  Button,
  CircularProgress,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { Check } from '@mui/icons-material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { MAXIMUM_TEXT_LENGTH } from 'common/types/constants.ts';
import { theme } from '@store';

interface ImproveCareNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalCareNotes: string;
  onKeepOriginal: () => void;
  onUseIt: (suggestion: string) => void;
  aiSuggestedText: string;
  isLoading: boolean;
  // No caller opts in today: the modal is only mounted once a suggestion has
  // arrived, and the wait is shown on the complete confirmation modal instead.
  // Kept for a future flow that opens this modal before the suggestion exists.
  isAiGenerating?: boolean;
  isInsufficientContext?: boolean;
}

export const ImproveCareNotesModal: FC<ImproveCareNotesModalProps> = ({
  isOpen,
  onClose,
  originalCareNotes,
  onKeepOriginal,
  onUseIt,
  aiSuggestedText,
  isLoading,
  isAiGenerating = false,
  isInsufficientContext = false,
}) => {
  const [suggestedText, setSuggestedText] = useState(aiSuggestedText);

  useEffect(() => {
    if (aiSuggestedText) {
      setSuggestedText(aiSuggestedText);
    }
  }, [aiSuggestedText]);

  const handleOnUseIt = () => {
    onUseIt(suggestedText);
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      disableCloseOnOutsideClick={isLoading}
      title={t('worklist.worklistItem.improveCareNotesModal.title')}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {isInsufficientContext && (
          <StatusBanner
            variant='lightWarning'
            text={t(
              'worklist.worklistItem.improveCareNotesModal.insufficientContextTitle',
            )}
            subText={t(
              'worklist.worklistItem.improveCareNotesModal.insufficientContextMessage',
            )}
            textFontSize='16px'
            textFontWeight={600}
            noBottomMargin
          />
        )}

        <TextField
          minRows={3}
          variant='outlined'
          label={t(
            'worklist.worklistItem.improveCareNotesModal.originalTextLabel',
          )}
          value={originalCareNotes}
          multiline
          disabled
        />

        {isAiGenerating ? (
          <Box
            sx={{
              background:
                'linear-gradient(225deg, #008B5B, #ABE1CE, #133F6E, #008B5B)',
              backgroundSize: '300%',
              p: '2px',
              animation: 'linear-bg 3s infinite alternate',
              borderRadius: '4px',
            }}
          >
            <Box
              sx={{
                background: '#fff',
                p: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                borderRadius: '4px',
              }}
            >
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                <AutoFixHighIcon sx={{ fontSize: '1rem' }} color='primary' />
                <Typography
                  variant='body2'
                  sx={{
                    color: theme.palette.grey[600],
                  }}
                >
                  {t('worklist.worklistItem.improveCareNotesModal.generating')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Skeleton variant='text' width='100%' height={16} />
                <Skeleton variant='text' width='92%' height={16} />
                <Skeleton variant='text' width='96%' height={16} />
                <Skeleton variant='text' width='78%' height={16} />
              </Box>
            </Box>
          </Box>
        ) : (
          <AISuggestedText
            title={t(
              'worklist.worklistItem.improveCareNotesModal.suggestedTitle',
            )}
            value={suggestedText}
            onChange={setSuggestedText}
            disabled={isLoading}
            charLimit={MAXIMUM_TEXT_LENGTH}
          />
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            p: '24px 0',
            position: 'sticky',
            bottom: 0,
            left: 0,
            background: '#fff',
          }}
        >
          <Button
            onClick={onKeepOriginal}
            disabled={isLoading || isAiGenerating}
          >
            {t('worklist.worklistItem.improveCareNotesModal.keepOriginalText')}
          </Button>
          <Button
            variant='contained'
            color='secondary'
            startIcon={
              isLoading ? (
                <CircularProgress size={16} color='inherit' />
              ) : (
                <Check sx={{ width: 16, height: 16 }} />
              )
            }
            onClick={handleOnUseIt}
            disabled={
              isAiGenerating ||
              !suggestedText ||
              isLoading ||
              suggestedText.length > MAXIMUM_TEXT_LENGTH
            }
            sx={{ color: 'white' }}
          >
            {t('worklist.worklistItem.improveCareNotesModal.useIt')}
          </Button>
        </Box>
      </Box>
    </CustomModal>
  );
};
